import type { ClientMessage } from '../../shared/signaling.js';
import type { FileMeta } from '../../shared/transfer.js';
import { formatBytes } from './format.js';
import { t } from './i18n/index.js';
import { runReceiveSession } from './receive-session.js';
import type { JoinTarget } from './route.js';
import type { FileSink, SinkKind } from './sinks/file-sink.js';
import { SaveCancelledError } from './sinks/file-sink.js';
import { createSink, detectSinkKind } from './sinks/index.js';
import { clear, el, hide, show } from './ui/dom.js';
import { createFileCard } from './ui/file-card.js';
import { createOutcomeCard } from './ui/outcome-card.js';
import { createHeader, createErrorCard, userFacingError } from './ui/shell.js';
import { createStatusPill } from './ui/status.js';

type ReceiverUi = Readonly<{
  status: ReturnType<typeof createStatusPill>;
  fileHost: HTMLElement;
  destinationHost: HTMLElement;
  progressHost: HTMLElement;
  resultHost: HTMLElement;
}>;

const destinationCopy = (kind: SinkKind, size: number): Readonly<{ button: string; text: string }> => {
  switch (kind) {
    case 'file-system-access':
      return { button: t('dest.picker.button'), text: t('dest.picker.text', { size: formatBytes(size) }) };
    case 'service-worker':
      return { button: t('dest.worker.button'), text: t('dest.worker.text', { size: formatBytes(size) }) };
    case 'memory':
      return { button: t('dest.memory.button'), text: t('dest.memory.text', { size: formatBytes(size) }) };
  }
};

/** Asks the user where to put the file. The sender waits until this resolves. */
export const promptForDestination = (meta: FileMeta, host: HTMLElement): Promise<FileSink> =>
  new Promise((resolve, reject) => {
    const kind = detectSinkKind();
    const copy = destinationCopy(kind, meta.size);
    const button = el('button', { className: 'button button--large', type: 'button', text: copy.button });
    const note = el('div', { className: 'share__note' });
    button.addEventListener('click', () => {
      button.disabled = true;
      createSink(kind, meta)
        .then(resolve)
        .catch((error: unknown) => {
          button.disabled = false;
          if (error instanceof SaveCancelledError) {
            note.textContent = t('dest.cancelled');
            note.className = 'share__note share__note--warn';
            return;
          }
          reject(error instanceof Error ? error : new Error(String(error)));
        });
    });
    host.replaceChildren(
      el('section', { className: 'card' }, [
        el('div', { className: 'card__title', text: t('dest.title') }),
        el('p', { className: 'card__text', text: copy.text }),
        button,
        note,
      ]),
    );
  });

export const joinMessageFor = (target: JoinTarget): ClientMessage =>
  target.kind === 'room' ? { type: 'join', roomId: target.roomId } : { type: 'join-by-pin', pin: target.pin };

const runReceive = async (target: JoinTarget, ui: ReceiverUi): Promise<void> => {
  const received = await runReceiveSession(joinMessageFor(target), {
    onConnected: () => ui.status.set('active', t('receiver.status.connected')),
    onChannelOpen: () => ui.status.set('active', t('receiver.status.channelOpen')),
    chooseSink: async (meta) => {
      ui.fileHost.replaceChildren(createFileCard(meta));
      ui.status.set('waiting', t('receiver.status.chooseDestination'));
      const sink = await promptForDestination(meta, ui.destinationHost);
      clear(ui.destinationHost);
      return sink;
    },
    onTransferStart: (progress) => {
      ui.progressHost.replaceChildren(progress.element);
      ui.status.set('active', t('receiver.status.receiving'));
    },
  });
  ui.status.set('success', t('receiver.status.complete'));
  ui.resultHost.replaceChildren(createOutcomeCard(received, true));
};

export const renderReceiverPage = (root: HTMLElement, target: JoinTarget): void => {
  clear(root);
  const main = el('main', { className: 'page' });
  const ui: ReceiverUi = {
    status: createStatusPill(),
    fileHost: el('div'),
    destinationHost: el('div'),
    progressHost: el('div'),
    resultHost: el('div'),
  };
  const errorHost = el('div');
  ui.status.set('waiting', t('receiver.status.contacting'));
  show(ui.status.element);

  main.append(
    createHeader(t('receiver.tagline')),
    ui.fileHost,
    ui.status.element,
    ui.destinationHost,
    ui.progressHost,
    ui.resultHost,
    errorHost,
  );
  root.append(main);

  runReceive(target, ui).catch((error: unknown) => {
    ui.status.set('error', t('status.failed'));
    hide(ui.progressHost);
    clear(ui.destinationHost);
    errorHost.replaceChildren(createErrorCard(userFacingError(error), t('action.retry'), () => window.location.reload()));
  });
};
